import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import { RainbowButton } from "@/components/ui/rainbow-button";
import { Loader2 } from "lucide-react";
import InterviewAnalysis from './InterviewAnalysis';
import { StarsBackground } from './ui/stars-background';
import { AnalysisNav } from './ui/analysis-nav';
import { InterviewAnalysisProgress } from './ui/InterviewAnalysisProgress';

// Define the shape of the analysis data
type AnalysisData = {
  content: string;
  created_at: string;
  version_type: string;
};

// Define the shape of the interview analysis data
type InterviewAnalysisData = {
  id: number;
  analysis_id: string;
  question: string;
  transcription: string;
  analysis_content: any;
  overall_score: number | null;
  summary: string | null;
  created_at: string;
};

interface InterviewQuestions {
  key_questions: string[];
  followup: {
    market: string[];
    traction: string[];
  };
}

const WebRTCInterview = () => {
  const { id } = useParams();
  const [isRecording, setIsRecording] = useState(false);
  const [questions, setQuestions] = useState<InterviewQuestions | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [analysis, setAnalysis] = useState<AnalysisData | null>(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [transcriptions, setTranscriptions] = useState<string[]>([]);
  // Audio visualization state
  const [isVCSpeaking, setIsVCSpeaking] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  
  // Timer state
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  
  // Countdown state
  const [countdown, setCountdown] = useState<number | null>(null);
  
  // Credits and error state
  const [credits, setCredits] = useState(0);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Previous analyses state
  const [previousAnalyses, setPreviousAnalyses] = useState<InterviewAnalysisData[]>([]);
  const [loadingPreviousAnalyses, setLoadingPreviousAnalyses] = useState(false);
  
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const transcriptContainerRef = useRef<HTMLDivElement | null>(null);

  // Compute current question from questions and index
  const currentQuestion = questions?.key_questions[currentQuestionIndex] || '';
  
  // Effect to scroll transcript to bottom when new messages arrive
  useEffect(() => {
    if (transcriptContainerRef.current && transcriptions.length > 0) {
      const container = transcriptContainerRef.current;
      // Use a small timeout to ensure the DOM has updated with the new message
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
      }, 100);
    }
  }, [transcriptions]);

  const fetchCredits = async () => {
    try {
      setCreditsLoading(true);
      const response = await api.get('/api/credits/balance');
      setCredits(response.data.credits);
    } catch (err) {
      console.error('Error fetching credits:', err);
    } finally {
      setCreditsLoading(false);
    }
  };

  // Fetch previous interview analyses
  const fetchPreviousAnalyses = async () => {
    if (!id) return;
    
    try {
      setLoadingPreviousAnalyses(true);
      const response = await api.get(`/api/interview_analyses/${id}`);
      
      if (response.data.status === 'success' && response.data.analyses) {
        setPreviousAnalyses(response.data.analyses);
      }
    } catch (error) {
      console.error('Error fetching previous analyses:', error);
    } finally {
      setLoadingPreviousAnalyses(false);
    }
  };

  useEffect(() => {
    const initializeInterview = async () => {
      try {
        const response = await api.get(`/api/interview/${id}`);
        setQuestions(response.data.questions);
        await fetchCredits();
        await fetchPreviousAnalyses();
      } catch (error) {
        console.error('Error initializing interview:', error);
      }
    };

    initializeInterview();
    return () => {
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      if (audioElementRef.current) {
        audioElementRef.current.srcObject = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [id]);

  // Function to set up audio visualization
  const setupAudioVisualization = (audioElement: HTMLAudioElement) => {
    try {
      // Create audio context
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Create analyzer
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      
      // Connect audio element to analyzer
      const source = audioContext.createMediaElementSource(audioElement);
      source.connect(analyser);
      analyser.connect(audioContext.destination);
      
      // Start visualization loop
      visualize();
      
      // Set VC as speaking when audio starts
      audioElement.addEventListener('play', () => {
        setIsVCSpeaking(true);
      });
      
      // Set VC as not speaking when audio ends
      audioElement.addEventListener('ended', () => {
        setIsVCSpeaking(false);
        setAudioLevel(0);
      });
      
      // Also handle pause events
      audioElement.addEventListener('pause', () => {
        setIsVCSpeaking(false);
        setAudioLevel(0);
      });
    } catch (error) {
      console.error('Error setting up audio visualization:', error);
    }
  };
  
  // Function to visualize audio
  const visualize = () => {
    if (!analyserRef.current) return;
    
    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const updateVisualization = () => {
      if (!analyserRef.current) return;
      
      analyser.getByteFrequencyData(dataArray);
      
      // Calculate average level
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += dataArray[i];
      }
      const avg = sum / bufferLength;
      
      // Normalize to 0-100 range
      const normalizedLevel = Math.min(100, Math.max(0, avg * 1.5));
      setAudioLevel(normalizedLevel);
      
      // Continue animation loop
      animationFrameRef.current = requestAnimationFrame(updateVisualization);
    };
    
    // Start animation loop
    animationFrameRef.current = requestAnimationFrame(updateVisualization);
  };

  const initializeWebRTC = async () => {
    try {
      // Check if user has enough credits
      const accessCheck = await api.get('/api/credits/check-access/investor-interview');
      
      if (!accessCheck.data.can_access) {
        setError(accessCheck.data.message);
        return;
      }

      // Use credits for the feature
      const useCredits = await api.post('/api/credits/use-feature/investor-interview');
      
      if (!useCredits.data.success) {
        setError(useCredits.data.message);
        return;
      }

      // Get interview details first
      const interviewResponse = await api.get(`/api/interview/${id}`);
      const { company_name, founder_name, questions: interviewQuestions } = interviewResponse.data;
      
      // Set questions in state
      setQuestions(interviewQuestions);
      
      // Get ephemeral key from the server
      const tokenResponse = await api.get('/api/session');
      const EPHEMERAL_KEY = tokenResponse.data.client_secret.value;

      // Create peer connection
      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;

      // Set up audio element for remote audio
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioElementRef.current = audioEl;
      document.body.appendChild(audioEl);
      
      pc.ontrack = (e) => {
        if (audioElementRef.current) {
          audioElementRef.current.srcObject = e.streams[0];
          
          // Set up audio visualization after a short delay to ensure audio is loaded
          setTimeout(() => {
            setupAudioVisualization(audioElementRef.current!);
          }, 1000);
        }
      };

      // Add local audio track
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      // Set up data channel
      const dc = pc.createDataChannel('oai-events');
      dataChannelRef.current = dc;
      
      // We'll handle transcription in the initial connection instead of updating later
      
      dc.addEventListener('message', (e) => {
        // Store transcription data
        if (e.data && typeof e.data === 'string' && e.data.trim() !== '') {
          try {
            const message = JSON.parse(e.data);
            
            // Handle different transcript message types
            if (message.type === 'transcript') {
              // Add VC prefix to assistant transcripts
              const formattedTranscript = `VC: ${message.text}`;
              setTranscriptions(prev => {
                const newTranscriptions = [...prev, formattedTranscript];
                return newTranscriptions;
              });
            } else if (message.type === 'conversation.item.input_audio_transcription.completed') {
              // Extract the transcript text
              if (message.transcript) {
                const userTranscript = message.transcript;
                
                // Add formatted user transcript to the list
                if (userTranscript && userTranscript.trim() !== '') {
                  const formattedTranscript = `You: ${userTranscript}`;
                  setTranscriptions(prev => {
                    const newTranscriptions = [...prev, formattedTranscript];
                    return newTranscriptions;
                  });
                }
              }
            } else if (message.type === 'conversation.item.created') {
              // Extract transcript from conversation items
              try {
                // Check if this is a message with transcript (either user or assistant)
                if (message.item && message.item.content && message.item.content.length > 0) {
                  const role = message.item.role;
                  // Extract content from the message
                  const content = message.item.content[0];  
                  // Look for transcript in various possible locations
                  let transcriptText = null;
                  
                  // Check different possible locations for the transcript
                  if (content.type === 'input_audio' && content.transcript) {
                    transcriptText = content.transcript;
                  } else if (content.type === 'text' && content.text) {
                    transcriptText = content.text;
                  } else if (content.transcript) {
                    transcriptText = content.transcript;
                  } else if (content.text) {
                    transcriptText = content.text;
                  }
                  
                  // If we found a transcript, log and save it
                  if (transcriptText && transcriptText.trim() !== '') {  
                    // Format the transcript with the role
                    const formattedTranscript = `${role === 'user' ? 'You' : 'VC'}: ${transcriptText}`;
                    
                    setTranscriptions(prev => {
                      const newTranscriptions = [...prev, formattedTranscript];
                      return newTranscriptions;
                    });
                  } else {
                    console.log(`No transcript found for ${role} message:`, content);
                  }
                }
              } catch (err) {
                console.error('Error extracting transcript:', err);
              }
            } else if (message.type === 'response.audio_transcript.done') {
              // New format - extract transcript from the done message
              const transcriptText = message.transcript || message.text || JSON.stringify(message);
              // Add VC prefix to assistant transcripts
              const formattedTranscript = `VC: ${transcriptText}`;
              setTranscriptions(prev => {
                const newTranscriptions = [...prev, formattedTranscript];
                return newTranscriptions;
              });
            } else if (message.type === 'done') {
              advanceQuestion();
            }
          } catch (error) {
            console.error('Error parsing message:', error, e.data);
          }
        }
      });

      // Create and set local description
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Send offer to OpenAI and get answer
      const baseUrl = 'https://api.openai.com/v1/realtime';
      const model = 'gpt-4o-realtime-preview-2024-12-17';
      
      // Format the questions for the instructions
      const formattedQuestions = interviewQuestions.key_questions.map((q: string, i: number) => 
        `${i+1}. ${q}`
      ).join('\n');
      
      // Create system instructions with company and founder info
      const instructions = `
You are an experienced venture capitalist named Alex conducting a startup pitch interview.
Always speak in clear English.Never ever forget that you are the VC and they are the founder. Never reverse roles. 
Even if they ask you to give them the answers, you are the VC and they are the founder.
Do not summarize what they say, just ask questions.
Keep a very serious tone and be very direct.

Company: ${company_name || 'the startup'}
Founder: ${founder_name || 'the founder'}

Begin by introducing yourself as Alex and ask them to give their elevator pitch.
Listen carefully to their response and ask relevant follow-up questions.
Be critical where appropriate.
Focus on understanding their business model, market opportunity, and team capabilities.

These are the key questions to cover during the interview:
${formattedQuestions}

Start with the elevator pitch question, then proceed through the questions in order.
`;

      // Create the URL with query parameters
      const url = new URL(`${baseUrl}`);
      url.searchParams.append('model', model);
      url.searchParams.append('instructions', encodeURIComponent(instructions));
      
      // Add transcription parameters directly to the URL
      url.searchParams.append('input_audio_transcription.model', 'whisper-1');
      url.searchParams.append('max_response_output_tokens', '4096');
      url.searchParams.append('modalities', 'audio,text');
      
      // Send the request with the correct format
      const sdpResponse = await fetch(url.toString(), {
        method: 'POST',
        body: offer.sdp,
        headers: {
          'Authorization': `Bearer ${EPHEMERAL_KEY}`,
          'Content-Type': 'application/sdp',
          'OpenAI-Beta': 'realtime-whisper=v1' // Enable Whisper transcription
        }
      });
      
      // Add event listener for data channel open to send session.update
      dc.addEventListener('open', () => {
        // Wait a short moment to ensure connection is stable
        setTimeout(() => {
          try {
            // Create a simplified version of the instructions for the session.update
            const simplifiedInstructions = `You are a VC interviewing a founder about ${company_name || 'their startup'}. Ask questions to understand their business.`;
            
            // Create a session.update message based on the example code
            const sessionUpdateMessage = {
              'event_id': `event_${Date.now()}`,
              'type': 'session.update',
              'session': {
                'modalities': ['text', 'audio'],
                'instructions': simplifiedInstructions,
                'input_audio_format': 'pcm16',
                'output_audio_format': 'pcm16',
                'input_audio_transcription': {
                  'model': 'whisper-1'
                },
                'turn_detection': {
                  'type': 'server_vad',
                  'threshold': 0.5,
                  'prefix_padding_ms': 300,
                  'silence_duration_ms': 1000,
                  'create_response': true
                },
                'temperature': 0.8,
                'max_response_output_tokens': 4096
              }
            };
            
            // Send the session update message
            dc.send(JSON.stringify(sessionUpdateMessage));
          } catch (error) {
            console.error('Error sending session.update message:', error);
          }
        }, 1000);
      });

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text();
        throw new Error(`HTTP error! Status: ${sdpResponse.status}, Details: ${errorText}`);
      }

      const sdpText = await sdpResponse.text();

      const answer: RTCSessionDescriptionInit = {
        type: 'answer' as RTCSdpType,
        sdp: sdpText,
      };
      await pc.setRemoteDescription(answer);

      setIsRecording(true);
    } catch (error) {
      console.error('Error initializing WebRTC:', error);
    }
  };

  const analyzeInterview = async () => {
    try {
      // Show the analysis progress animation
      setIsAnalyzing(true);
      
      // Combine all transcriptions and analyze the complete interview
      const fullTranscription = transcriptions.join(' ');
      
      // If transcription is empty, create a placeholder
      const conversationHistory = fullTranscription.trim() || 
        "The founder discussed their startup idea, focusing on the product, market opportunity, and business model.";

      // Use the correct endpoint and data structure that matches Interview.tsx
      const response = await api.post('/api/analyze-interview', {
        analysis_id: id,
        conversation_history: conversationHistory
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Update to match the response structure from Interview.tsx
      if (response.data && response.data.raw_analysis) {
        // Check the type of raw_analysis
        const rawAnalysis = response.data.raw_analysis;
        // Set the analysis state based on the type
        setAnalysis(rawAnalysis);
        // The animation will handle setting showAnalysis to true
      } else {
        console.error('No analysis data in response:', response.data);
        setIsAnalyzing(false);
        // Handle the error case
      }
    } catch (error) {
      console.error('Error analyzing interview:', error);
      setIsAnalyzing(false);
    }
  };
  
  // Handler for when analysis animation completes
  const handleAnalysisComplete = (success: boolean) => {
    setIsAnalyzing(false);
    if (success) {
      setShowAnalysis(true);
    }
  };

  // Format seconds into MM:SS format
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (timerActive) {
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else if (!timerActive && interval) {
      clearInterval(interval);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerActive]);

  const startInterview = async () => {
    // Start the interview immediately and show countdown
    setIsRecording(true);
    setElapsedTime(0);
    setTimerActive(true);
    
    // Start the countdown from 5
    setCountdown(5);
    
    // Initialize WebRTC immediately so the mic can be ready by the time countdown finishes
    // This runs in parallel with the countdown
    initializeWebRTC();
    
    // Create an interval that decrements the countdown every second
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          // When countdown reaches 0, just clear the interval
          clearInterval(countdownInterval);
          setCountdown(null);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopInterview = async () => {
    try {
      // Stop the timer
      setTimerActive(false);
      
      // Close WebRTC connection
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      
      // Clean up audio element
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.srcObject = null;
      }
      
      // Set analyzing state
      setIsAnalyzing(true);
      
      // Send analysis request to backend
      const response = await api.post(`/api/interview/${id}/analyze`, {
        transcriptions
      });
      
      if (response.data.status === 'success') {
        setAnalysis(response.data.analysis);
        setShowAnalysis(true);
      } else {
        setError('Failed to analyze interview.');
      }
    } catch (error) {
      console.error('Error stopping interview:', error);
      setError('An error occurred while analyzing the interview.');
    } finally {
      setIsRecording(false);
      setIsAnalyzing(false);
    }
  };

  const advanceQuestion = () => {
    if (!questions) return;
    
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex < questions.key_questions.length) {
      // Update the current question index
      setCurrentQuestionIndex(nextIndex);
      
      // Send the next question to the model via data channel
      if (dataChannelRef.current && dataChannelRef.current.readyState === 'open') {
        const nextQuestion = questions.key_questions[nextIndex];
        dataChannelRef.current.send(JSON.stringify({
          type: 'next_question',
          question: nextQuestion
        }));
      }
    } else {
      stopInterview();
    }
  };



  if (isAnalyzing) {
    return <InterviewAnalysisProgress onComplete={handleAnalysisComplete} />;
  }

  // Extract overall score from analysis if it exists
  const getOverallScore = () => {
    if (typeof analysis === 'object' && analysis !== null) {
      // If analysis is an object with overall_score property
      if ('overall_score' in analysis) {
        return (analysis as any).overall_score;
      }
      // If it's the full analysis response with data property
      if ('data' in analysis && typeof analysis.data === 'object' && analysis.data !== null && 'overall_score' in analysis.data) {
        return (analysis.data as any).overall_score;
      }
    }
    return undefined;
  };

  if (showAnalysis && analysis) {
    const overallScore = getOverallScore();
    
    return (
      <main className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnalysisNav id={id!} />
          <div className="py-6">
            {typeof analysis === 'string' ? (
              <InterviewAnalysis 
                markdown={analysis} 
                overallScore={overallScore} 
              />
            ) : analysis.content ? (
              <InterviewAnalysis 
                markdown={analysis.content} 
                overallScore={overallScore} 
              />
            ) : (
              <InterviewAnalysis 
                markdown={JSON.stringify(analysis, null, 2)} 
                overallScore={overallScore} 
              />
            )}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background">
      {/* Stars Background */}
      <div className="fixed inset-0 -z-10">
        <StarsBackground />
      </div>
      
      {/* Countdown Overlay */}
      {countdown !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-md">
          <div className="text-center bg-white/90 p-12 rounded-xl shadow-2xl max-w-md mx-auto">
            <h2 className="text-4xl font-bold mb-6 text-gray-800">Get Ready!</h2>
            <div className="flex items-center justify-center w-40 h-40 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white text-7xl font-bold animate-pulse my-8 mx-auto">
              {countdown}
            </div>
            <p className="text-xl text-gray-600 mt-6">Gather your thoughts, be yourself.</p>
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnalysisNav id={id!} />
        
        <div className="py-6 max-w-4xl mx-auto">
          {!isRecording ? (
            <>
              <div className="flex flex-col items-center my-12">
                {error && (
                  <div className="text-red-500 mb-4">{error}</div>
                )}
                {credits < 15 && (
                  <div className="text-center mb-4">
                    <a href="/credits" className="text-blue-500 hover:text-blue-700">
                      Get more credits
                    </a>
                  </div>
                )}
                <RainbowButton
                  onClick={startInterview}
                  disabled={creditsLoading || credits < 15}
                  className={`mt-2 ${credits < 15 ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                >
                  {creditsLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : credits < 15 ? (
                    <div className="flex items-center justify-center">
                      <span className="text-red-500">Need More Credits</span>
                    </div>
                  ) : (
                    'Begin VC Interview (15 Credits)'
                  )}
                </RainbowButton>
              </div>
              
              {/* Previous Analyses Section - Only shown before starting an interview */}
              {previousAnalyses.length > 0 && (
                <section className="mt-16">
                  <h3 className="text-xl font-semibold mb-6 text-gray-800" style={{ fontSize: '16px', fontFamily: '"Open Sans", sans-serif' }}>Previous Analyses</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {previousAnalyses.map((analysis) => {
                      // Determine signal color and text based on overall_score
                      let signalColor = "bg-gray-400"; // Default neutral
                      let signalText = "Neutral";
                      let analysisTitle = "";
                      let analysisExcerpt = "";
                      let overallScore = analysis.overall_score;
                      
                      // Set signal color and text based on the overall_score
                      if (overallScore !== null && overallScore !== undefined) {
                        if (overallScore > 7) {
                          signalColor = "bg-green-500";
                          signalText = "Positive";
                        } else if (overallScore < 5) {
                          signalColor = "bg-red-500";
                          signalText = "Needs Improvement";
                        }
                      }
                      
                      // Use summary directly if available
                      if (analysis.summary) {
                        analysisExcerpt = analysis.summary;
                      }
                      
                      // Extract meaningful information from the analysis content if needed
                      if (analysis.analysis_content && !analysisExcerpt) {
                        try {
                          // Handle string content (might be JSON)
                          if (typeof analysis.analysis_content === 'string') {
                            try {
                              const parsedContent = JSON.parse(analysis.analysis_content);
                              // Only use parsed content for title/excerpt, not for score which we already have
                              
                              // Extract a meaningful title and excerpt
                              if (parsedContent.title) {
                                analysisTitle = parsedContent.title;
                              } else if (parsedContent.executive_summary) {
                                analysisTitle = "Executive Summary";
                                analysisExcerpt = parsedContent.executive_summary.substring(0, 100) + '...';
                              } else if (parsedContent.summary) {
                                analysisTitle = "Summary";
                                analysisExcerpt = parsedContent.summary.substring(0, 100) + '...';
                              }
                            } catch (e) {
                              // Not valid JSON, use as is
                              if (analysis.analysis_content.length > 0) {
                                analysisExcerpt = analysis.analysis_content.substring(0, 100) + '...';
                              }
                            }
                          } 
                          // Handle object content
                          else if (typeof analysis.analysis_content === 'object') {
                            const content = analysis.analysis_content;
                            // Only use content for title/excerpt, not for score which we already have
                            
                            // Extract meaningful content
                            if (content.title) {
                              analysisTitle = content.title;
                            } else if (content.executive_summary) {
                              analysisTitle = "Executive Summary";
                              analysisExcerpt = typeof content.executive_summary === 'string' 
                                ? content.executive_summary.substring(0, 100) + '...' 
                                : 'Analysis available';
                            } else if (content.summary) {
                              analysisTitle = "Summary";
                              analysisExcerpt = typeof content.summary === 'string' 
                                ? content.summary.substring(0, 100) + '...' 
                                : 'Analysis available';
                            } else if (content.key_insights) {
                              analysisTitle = "Key Insights";
                              analysisExcerpt = typeof content.key_insights === 'string' 
                                ? content.key_insights.substring(0, 100) + '...' 
                                : 'Analysis available';
                            }
                          }
                        } catch (error) {
                          console.error('Error parsing analysis content:', error);
                        }
                      }
                      
                      // If we couldn't extract a title, use the question
                      if (!analysisTitle && analysis.question) {
                        analysisTitle = analysis.question;
                      }
                      
                      // If we still don't have a title, use a default
                      if (!analysisTitle) {
                        analysisTitle = "Interview Analysis";
                      }
                      
                      // Format the date
                      const date = new Date(analysis.created_at);
                      const formattedDate = date.toLocaleString();
                      
                      return (
                        <div key={analysis.id} className="relative bg-transparent border border-gray-200 shadow-lg backdrop-blur-sm p-4 rounded-lg hover:border-blue-300 transition-colors cursor-pointer" 
                             onClick={() => {
                               // Navigate to analysis detail view or show modal with full analysis
                               console.log('View analysis clicked:');
                               // Future enhancement: Add navigation or modal display here
                             }}>
                          <div className="absolute inset-0 -z-10 overflow-hidden rounded-lg">
                            <StarsBackground />
                          </div>
                          <div className="relative z-10 flex items-center justify-between">
                            <div className="flex items-center">
                              <span className={`px-2 py-0.5 rounded-md font-medium ${
                                signalColor === "bg-green-500" ? 'bg-green-100 text-green-800' : 
                                signalColor === "bg-red-500" ? 'bg-red-100 text-red-800' : 
                                'bg-gray-100 text-gray-800'
                              }`}>{signalText}</span>
                              {/* Display the score from the direct property first, fall back to extracted score */}
                              {(analysis.overall_score !== null && analysis.overall_score !== undefined) ? (
                                <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-800 rounded-full font-medium" style={{ fontSize: '16px' }}>
                                  Score: {typeof analysis.overall_score === 'number' ? analysis.overall_score.toFixed(1) : analysis.overall_score}
                                </span>
                              ) : overallScore !== null && (
                                <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-800 rounded-full font-medium" style={{ fontSize: '16px' }}>
                                  Score: {typeof overallScore === 'number' ? overallScore.toFixed(1) : overallScore}
                                </span>
                              )}
                            </div>
                            <span className="text-gray-500" style={{ fontSize: '16px' }}>{formattedDate}</span>
                          </div>
                          <h4 className="mt-2 font-medium text-gray-800 truncate" style={{ fontSize: '16px', fontFamily: '"Open Sans", sans-serif' }}>{analysisTitle}</h4>
                          {/* Display the summary from the direct property first */}
                          {analysis.summary ? (
                            <p className="mt-1 text-gray-600 line-clamp-2" style={{ fontSize: '16px', fontFamily: '"Open Sans", sans-serif' }}>{analysis.summary}</p>
                          ) : analysisExcerpt && (
                            <p className="mt-1 text-gray-600 line-clamp-2" style={{ fontSize: '16px', fontFamily: '"Open Sans", sans-serif' }}>{analysisExcerpt}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </>
          ) : (
            <>
              {/* Two-column layout for interview and transcripts */}
              <div className="flex flex-col md:flex-row gap-6">
                {/* Left column - Interview UI */}
                <div className="md:w-7/12">
                  <section className="mb-8">
                    <div className="relative bg-transparent border border-gray-200 shadow-lg backdrop-blur-sm p-6 rounded-lg">
                      <div className="absolute inset-0 -z-10 overflow-hidden rounded-lg">
                        <StarsBackground />
                      </div>
                      <div className="relative z-10">
                        {/* Simple Voice Recording UI */}
                        <div className="flex flex-col items-center justify-center mb-6">
                          {/* Microphone Icon */}
                          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
                              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"></path>
                              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                              <line x1="12" x2="12" y1="19" y2="22"></line>
                            </svg>
                          </div>
                          
                          {/* Timer Display */}
                          <div className="text-center mb-6">
                            <div className="flex items-center justify-center mb-2">
                              <div className={`w-3 h-3 rounded-full mr-2 ${isVCSpeaking ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                              <span className="text-xl font-medium text-gray-800">
                                {isVCSpeaking ? "Listening" : formatTime(elapsedTime)}
                              </span>
                            </div>
                            <div className="text-gray-600">Just say <span className="font-semibold">"Hi"</span>to start the interview</div>
                          </div>
                        </div>
                        
                        {/* CSS-based Audio Visualization - Always visible */}
                        <div className="w-full h-32 bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center">
                          {/* Keep the canvas for the actual audio processing */}
                          <canvas ref={canvasRef} className="absolute w-full h-full opacity-0" />
                          
                          {/* CSS-based animated wave bars using Tailwind animations */}
                          <div className="flex items-end justify-center space-x-1 h-24 w-full px-4">
                            {Array.from({ length: 40 }).map((_, i) => {
                              // Different properties based on position
                              const isEven = i % 2 === 0;
                              const isCenterish = i > 12 && i < 28;
                              
                              // Different animation speeds
                              let animationClass = isEven ? 'animate-pulse' : 'animate-bounce';
                              
                              // Different animation durations
                              const animationDuration = 0.5 + (i % 3) * 0.3;
                              
                              // Different colors
                              const colorClass = isVCSpeaking ? 'bg-blue-500' : 'bg-gray-400';
                              
                              return (
                                <div 
                                  key={i}
                                  className={`${colorClass} rounded-full w-1.5 ${animationClass} transition-all duration-300`}
                                  style={{
                                    animationDuration: `${animationDuration}s`,
                                    animationDelay: `${i * 0.03}s`,
                                    height: isVCSpeaking ? (isCenterish ? '60%' : '30%') : '10%'
                                  }}
                                />
                              );
                            })}
                          </div>
                        </div>
                        
                        <div className="flex justify-center mt-6">
                          <RainbowButton
                            onClick={stopInterview}
                            className="bg-red-500 hover:bg-red-600 text-white"
                          >
                            End Interview
                          </RainbowButton>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
                
                {/* Right column - Transcripts */}
                <div className="md:w-5/12">
                  {/* Current Interview Responses */}
                  <section className="sticky top-4">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Your Responses</h3>
                    <div 
                      ref={transcriptContainerRef} 
                      className="space-y-4 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar"
                    >
                      {transcriptions.length > 0 ? (
                        transcriptions.map((text, index) => {
                          // Determine if this is a user message (starts with "You:") or VC message (starts with "VC:")
                          const isUserMessage = text.startsWith('You:');
                          const isVCMessage = text.startsWith('VC:');
                          
                          // Set different styling based on the message type
                          let borderClass = 'border-gray-200';
                          let bgClass = 'bg-transparent';
                          
                          if (isUserMessage) {
                            borderClass = 'border-blue-300';
                            bgClass = 'bg-blue-50/30';
                          } else if (isVCMessage) {
                            borderClass = 'border-purple-300';
                            bgClass = 'bg-purple-50/30';
                          }
                          
                          return (
                            <div 
                              key={index} 
                              className={`relative ${bgClass} border ${borderClass} shadow-lg backdrop-blur-sm p-4 rounded-lg transition-all duration-300`}
                            >
                              <div className="absolute inset-0 -z-10 overflow-hidden rounded-lg">
                                <StarsBackground />
                              </div>
                              <p className="text-gray-800 relative z-10">
                                {isUserMessage && (
                                  <span className="font-semibold text-blue-600">{text.substring(0, 4)}</span>
                                )}
                                {isVCMessage && (
                                  <span className="font-semibold text-purple-600">{text.substring(0, 3)}</span>
                                )}
                                {isUserMessage && text.substring(4)}
                                {isVCMessage && text.substring(3)}
                                {!isUserMessage && !isVCMessage && text}
                              </p>
                            </div>
                          );
                        })
                      ) : (
                        <div className="relative bg-transparent border border-gray-200 shadow-lg backdrop-blur-sm p-4 rounded-lg">
                          <div className="absolute inset-0 -z-10 overflow-hidden rounded-lg">
                            <StarsBackground />
                          </div>
                          <p className="text-gray-500 italic relative z-10">Transcripts will appear here as you speak...</p>
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default WebRTCInterview;
